/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";

import { useCollectFieldsEndpoint } from "@/lib/ServerApi/useCollectFieldsEndpoint";
import {
  VerificationStepsEnum,
  MedicalProfessionalStatusDefaultMessagesEnum,
} from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  VerificationService,
  FormSelectChoice,
  Organization,
  MedicalProfessionalPersonalInfoViewModel,
  MedicalProfessionalPersonalInfoResponse,
  CustomFormFieldConfig,
} from "../../lib/types/types";
import {
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
  produceDraftViewModel,
  getStatusLabel,
  getAvailableMedicalStatuses,
  getFieldConfigByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";

import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PostalCodeComponent as PostalCode } from "../FormFields/PostalCode/PostalCodeComponent";
import { StatusComponent as MedicalStatus } from "../FormFields/Status/StatusComponent";
import { MedicalProfessionalOrganizationComponent as MedicalProfessionalOrganization } from "../FormFields/MedicalProfessionalOrganization/MedicalProfessionalOrganizationComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { MemberIdComponent as MemberId } from "../FormFields/MemberId/MemberIdComponent";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";

interface StepMedicalProfessionalPersonalInfoComponentProps {
  verificationService: VerificationService;
  intl: any;
}

type MemberIdFieldType =
  | "registrationNumber"
  | "numeroColegiado"
  | "autorisationsId"
  | "globalLocationNumber"
  | "gbrNummer";

const memberIdMessageKeysByCountry: Record<string, MemberIdFieldType> = {
  AT: "gbrNummer",
  ES: "numeroColegiado",
  DK: "autorisationsId",
  CH: "globalLocationNumber",
  IE: "registrationNumber",
  GB: "registrationNumber",
};

const getMemberIdMessageKeys = (country: string) => {
  const memberIdFieldType: MemberIdFieldType =
    memberIdMessageKeysByCountry[country] || "registrationNumber";
  return {
    labelKey: `step.collectMedicalPersonalInfo.${memberIdFieldType}Label`,
    explanationKey: `step.collectMedicalPersonalInfo.${memberIdFieldType}Explanation`,
  };
};

export const StepMedicalProfessionalPersonalInfo = ({
  intl,
  verificationService,
}: StepMedicalProfessionalPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
  const options = getOptions();

  const locale = viewModel.localeChoice.value;

  const updateMedicalProfessionalViewModel = (
    key: keyof MedicalProfessionalPersonalInfoViewModel,
    value: any,
  ) => {
    const nextState = produceDraftViewModel(viewModel, key, value);
    verificationService.updateViewModel(nextState);
  };

  const { isFieldOptional } = useCollectFieldsEndpoint({
    verificationId: verificationResponse.verificationId,
    currentStep: verificationResponse.currentStep,
    viewModel,
  });
  const shouldRenderMemberId = isFieldOptional("memberId");

  React.useEffect(() => {
    // If the memberId field is not rendered, we shouldn't validate it
    //  partially update to avoid clobbering the rest of the viewModel
    if (shouldRenderMemberId) {
      verificationService.updateViewModel({ fieldsToSkipValidation: [] }, { partial: true });
    } else {
      verificationService.updateViewModel(
        { fieldsToSkipValidation: ["memberId"] },
        { partial: true },
      );
    }
  }, [shouldRenderMemberId]);

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="medical-personal-info"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      <MedicalStatus
        segmentId="medical"
        value={{
          value: viewModel.status,
          label: getStatusLabel(
            intl,
            viewModel.status,
            MedicalProfessionalStatusDefaultMessagesEnum,
          ),
        }}
        isErrored={!!fieldValidationErrors.status}
        options={getAvailableMedicalStatuses(intl, verificationResponse.availableStatuses)}
        disabled={getFieldConfigByFieldId("status")?.disabled}
        onChange={(status: FormSelectChoice) => {
          updateMedicalProfessionalViewModel("status", status ? (status.value as string) : "");
          updateFieldValidationErrorsByFieldId(
            "status",
            status ? (status.value as string) : "",
            verificationService,
          );
        }}
      />
      <MedicalProfessionalOrganization
        isErrored={!!fieldValidationErrors.organization}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
        value={
          (verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).organization
        }
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("organization")?.disabled}
      />
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateMedicalProfessionalViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateMedicalProfessionalViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
        onChange={(newValue) => {
          updateMedicalProfessionalViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
      />
      <PostalCode
        isErrored={!!fieldValidationErrors.postalCode}
        onChange={(newValue) => {
          updateMedicalProfessionalViewModel("postalCode", newValue);
          updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
        }}
        value={viewModel.postalCode}
        disabled={getFieldConfigByFieldId("postalCode")?.disabled}
        explanation={
          <FormattedHTMLMessage
            id="postalCodeExplanation"
            defaultMessage="Use the location on file with your licensing body"
          />
        }
      />
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Needed to send you your unique code"
          />
        }
        onChange={(newValue) => {
          updateMedicalProfessionalViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateMedicalProfessionalViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}
      {shouldRenderMemberId && (
        <MemberId
          value={viewModel.memberId}
          isErrored={!!fieldValidationErrors.memberId}
          isRequired={getFieldConfigByFieldId("memberId")?.requireIfOptional}
          disabled={getFieldConfigByFieldId("memberId")?.disabled}
          onChange={(newValue) => {
            updateMedicalProfessionalViewModel("memberId", newValue);
            updateFieldValidationErrorsByFieldId("memberId", newValue, verificationService);
          }}
          explanation={
            <FormattedHTMLMessage
              id={getMemberIdMessageKeys(viewModel.country).explanationKey}
              defaultMessage="Your Professional Membership Registration Number"
            />
          }
          label={
            <FormattedHTMLMessage
              id={getMemberIdMessageKeys(viewModel.country).labelKey}
              defaultMessage="Registration Number"
            />
          }
        />
      )}
      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateMedicalProfessionalViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />
      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />
      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectMedicalProfessionalPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Medical Professional Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepMedicalProfessionalPersonalInfoComponent = injectIntl(
  StepMedicalProfessionalPersonalInfo,
);
